import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormField } from "../../../form/component/molecules/FormField";
import { TypeSelector } from "../../../form/component/molecules/TypeSelector";
import { Button } from "../../../dashboard/components/atom/Button";
import { Notification } from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import { API_BASE_URL } from "../../../config/api";

// Mantine Rich Text (TipTap)
import { RichTextEditor } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";

export function RecruitmentForm({ onSuccess }) {
  const [formData, setFormData] = useState({
    department: "",
    positionType: "",
    positioncategory: "",
    title: "",
    jobLevel: "",
    manager: "",
    motivationNote: "",
  });

  const [motivationAttachment, setAttachment] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const editor = useEditor({
    extensions: [StarterKit],
    content: "",
    onUpdate: ({ editor }) =>
      setFormData((prev) => ({ ...prev, motivationNote: editor.getHTML() })),
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e) => setAttachment(e.target.files[0]);

  // Base styles
  const ringYellow =
    "focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400";
  const inputBase =
    `w-full border border-gray-300 rounded px-2 py-1 text-black placeholder-gray-400 ${ringYellow}`;
  const selectBase =
    `w-full border border-gray-300 rounded px-2 py-1 font-normal ${ringYellow}`;
  const selectClass = (value) =>
    `${selectBase} ${value ? "text-black" : "text-gray-400"}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setIsSubmitting(true);

    const token = localStorage.getItem("token");
    if (!token) {
      setError("Not logged in. Please authenticate first.");
      setIsSubmitting(false);
      return;
    }

    if (
      !formData.department ||
      !formData.positionType ||
      !formData.positioncategory ||
      !formData.title ||
      !formData.jobLevel ||
      !formData.manager
    ) {
      setError("Please fill in all required fields.");
      setIsSubmitting(false);
      return;
    }

    let username = null;
    try {
      const decoded = JSON.parse(atob(token.split(".")[1] || ""));
      username = decoded.username;
      if (!username) throw new Error("Username not found in token");
    } catch {
      setError("Invalid token or missing username. Please log in again.");
      setIsSubmitting(false);
      return;
    }

    const form = new FormData();
    form.append("department", formData.department);
    form.append("positionType", formData.positionType);
    form.append("positionTitle", formData.title);
    form.append("jobLevel", formData.jobLevel);
    form.append("positioncategory", formData.positioncategory);
    form.append("hiringmanagersname", formData.manager);
    if (formData.motivationNote)
      form.append("motivationNote", formData.motivationNote);
    if (motivationAttachment)
      form.append("motivationAttachment", motivationAttachment);

    try {
      const res = await fetch(`${API_BASE_URL}/api/requests`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Submit failed (${res.status})`);
      }

      const data = await res.json();
      onSuccess?.(data);

      setFormData({
        department: "",
        positionType: "",
        positioncategory: "",
        title: "",
        jobLevel: "",
        manager: "",
        motivationNote: "",
      });
      setAttachment(null);
      editor?.commands.setContent("");

      setSuccess("Recruitment request submitted successfully!");
      setTimeout(() => navigate("/dashboard"), 1500);
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to submit");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full h-screen overflow-y-auto bg-white">
      <div className="max-w-[900px] mx-auto p-6 rounded-lg shadow-lg bg-white">
        {success && (
          <Notification
            icon={<IconCheck size={20} />}
            color="teal"
            title="Success"
            mt="md"
            onClose={() => setSuccess("")}
          >
            {success}
          </Notification>
        )}

        {error && (
          <Notification
            icon={<IconX size={20} />}
            color="red"
            title="Error"
            mt="md"
            onClose={() => setError("")}
          >
            {error}
          </Notification>
        )}

        {/* Tight but readable spacing */}
        <form onSubmit={handleSubmit} className="pb-6 mt-3 space-y-3">
          {/* Department */}
          <FormField label="Requesting Department">
            <select
              value={formData.department}
              onChange={(e) => handleChange("department", e.target.value)}
              className={selectClass(formData.department)}
            >
              <option value="" disabled>
                Select Department
              </option>
              <option className="text-black">Chief Executive Officer Office</option>
              <option className="text-black">Consumer</option>
              <option className="text-black">Corporate Services</option>
              <option className="text-black">CEX &amp; COP</option>
              <option className="text-black">Enterprise Business Unit</option>
              <option className="text-black">Finance</option>
              <option className="text-black">Human Resources</option>
              <option className="text-black">Information Technology</option>
              <option className="text-black">Internal Audit and Forensic Services</option>
              <option className="text-black">Network Technology</option>
              <option className="text-black">Risk and Compliance</option>
              <option className="text-black">Sales and Distribution</option>
              <option className="text-black">Fintech</option>
            </select>
          </FormField>

          {/* Position Type + Category */}
          <FormField label="Position Type">
            <select
              value={formData.positionType}
              onChange={(e) => handleChange("positionType", e.target.value)}
              className={selectClass(formData.positionType)}
            >
              <option value="" disabled>
                Select Position Type
              </option>
              <option value="Permanent" className="text-black">
                Permanent
              </option>
              <option value="Apprentice" className="text-black">
                Apprentice
              </option>
            </select>

            <TypeSelector
              value={formData.positioncategory}
              onChange={(value) => handleChange("positioncategory", value)}
              classNameOverride={{
                trigger: (val) =>
                  `${selectBase} ${val ? "text-black" : "text-gray-400"}`,
                listItem: "text-black",
              }}
            />
          </FormField>

          {/* Position Title */}
          <FormField label="Position Title">
            <input
              type="text"
              placeholder="Enter position title"
              value={formData.title}
              onChange={(e) => handleChange("title", e.target.value)}
              className={inputBase}
            />
          </FormField>

          {/* Job Level */}
          <FormField label="Job Level">
            <select
              value={formData.jobLevel}
              onChange={(e) => handleChange("jobLevel", e.target.value)}
              className={selectClass(formData.jobLevel)}
            >
              <option value="" disabled>
                Select Job Level
              </option>
              {["1L","1H","1M","2L","2H","2M","3L","3M","3H","4L","4M","4H"].map((lvl) => (
                <option key={lvl} className="text-black">{lvl}</option>
              ))}
            </select>
          </FormField>

          {/* Manager */}
          <FormField label="Hiring Manager’s Name">
            <input
              type="text"
              placeholder="Enter manager's name"
              value={formData.manager}
              onChange={(e) => handleChange("manager", e.target.value)}
              className={inputBase}
            />
          </FormField>

          <FormField label="Motivation Description (Optional)">
  <RichTextEditor editor={editor}>
    <RichTextEditor.Toolbar sticky stickyOffset={0}>
      <RichTextEditor.ControlsGroup>
        <RichTextEditor.Bold />
        <RichTextEditor.Italic />
        <RichTextEditor.Underline />
        <RichTextEditor.Strikethrough />
      </RichTextEditor.ControlsGroup>
      <RichTextEditor.ControlsGroup>
        <RichTextEditor.H1 />
        <RichTextEditor.H2 />
        <RichTextEditor.H3 />
      </RichTextEditor.ControlsGroup>
      <RichTextEditor.ControlsGroup>
        <RichTextEditor.BulletList />
        <RichTextEditor.OrderedList />
        <RichTextEditor.Blockquote />
        <RichTextEditor.Link />
      </RichTextEditor.ControlsGroup>
    </RichTextEditor.Toolbar>

    {/* Comfortable box size like your screenshot */}
    <RichTextEditor.Content
      style={{
        minHeight: 160,       // ~like your image, not too big
        padding: "8px",
      }}
    />
  </RichTextEditor>

  {/* Placeholder text (shows when empty) */}
  {!formData.motivationNote && (
    <p className="text-gray-400 text-sm mt-1">Describe required motivation</p>
  )}
</FormField>


          {/* Attachment (Optional) */}
          <FormField label="Motivation Attachment (Optional)">
            <input
              type="file"
              accept=".pdf,.doc,.docx,.txt"
              onChange={handleFileChange}
              className={`w-full text-sm ${ringYellow}`}
            />
          </FormField>

          <div className="flex justify-end mt-3 mb-4">
            <Button
              type="submit"
              label={isSubmitting ? "Submitting..." : "Submit Request"}
              disabled={isSubmitting}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
